import { ChangeEvent, FC, PropsWithChildren, useCallback, useMemo, useRef } from 'react';

import { CONSENT_CONFIG_ERROR, RequisitesFieldName } from 'utils/constants/requisites';

import { Cell, CellText, Checkbox, Link, Tooltip, useBreakpoint } from '@hh.ru/magritte-ui';

import { formatToReactComponent } from 'bloko/common/trl';

import useTrls from 'hooks/translation/useTrls';

import { Action } from 'store/createReducer';
import { setRequisiteByName, setRequisiteErrorByFieldPath } from 'store/models/requisites';
import { useDispatch, useSelector } from 'store/utils';

interface ConsentProps {
    isFieldDisabled: boolean;
}

const fieldName = RequisitesFieldName.Consent;

const Consent: FC<ConsentProps & PropsWithChildren> = ({ isFieldDisabled }) => {
    const { requisites, requisitesErrors } = useSelector((state) => state.requisites);
    const dispatch = useDispatch();

    const trl = useTrls();

    const fieldError = requisitesErrors[fieldName];
    const fieldErrorTrl = fieldError && CONSENT_CONFIG_ERROR[fieldError];

    const handleChange = useCallback(
        ({ target: { checked: value } }: ChangeEvent<HTMLInputElement>) => {
            const actions: Action[] = [setRequisiteByName({ fieldName, value })];
            if (fieldError) {
                actions.push(setRequisiteErrorByFieldPath({ fieldPath: fieldName, value: null }));
            }
            dispatch(actions);
        },
        [dispatch, fieldError]
    );

    const onClose = useCallback(() => {
        dispatch(setRequisiteErrorByFieldPath({ fieldPath: fieldName, value: null }));
    }, [dispatch]);

    const { isMobile } = useBreakpoint();

    const activatorRef = useRef<HTMLAnchorElement>(null);

    const content = useMemo(
        () =>
            formatToReactComponent(trl('RequisitesForm.field.consent'), {
                '[0]': (
                    <Link
                        typography="label-2-regular"
                        inline
                        style="accent"
                        target="_blank"
                        href="https://hh.ru/article/offer_advertising"
                        enableVisited={false}
                        ref={activatorRef}
                    >
                        {trl('RequisitesForm.field.consent.link')}
                    </Link>
                ),
            }),
        [trl]
    );

    const checkbox = (
        <Checkbox
            disabled={isFieldDisabled}
            onChange={handleChange}
            checked={!!requisites[fieldName]}
            data-qa="requisites-form-consent"
            invalid={!!fieldError}
        />
    );

    return (
        <Cell align="top" right={isMobile ? checkbox : undefined} left={isMobile ? undefined : checkbox}>
            <CellText>{content}</CellText>

            <Tooltip
                activatorRef={activatorRef}
                visible={!!fieldError}
                showClose
                onClose={onClose}
                placement="top-center"
            >
                {trl(fieldErrorTrl)}
            </Tooltip>
        </Cell>
    );
};

export default Consent;
